import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import ChartJs from 'chart.js/auto';
import classNames from 'classnames/bind';
import React, { useRef } from 'react';

import styles from './GPSRaw.module.scss';
import MiniTable from './MiniTable';

import { getCSSVariable } from '@/utils/theme';

const cx = classNames.bind(styles);

// X축 구간 수
const TICK_COUNT = 30;

const GPSRaw = ({
  latitude = 0,
  longitude = 0,
  altitude = 0,
  satellites = 0,
  eph = 0,
  epv = 0,
  time = 0,
  showData = false,
}) => {
  const satellitesRef = useRef(satellites);
  const chartRef = useRef();
  const chart = useRef();

  useMountEffect(() => {
    const intervalId = setInterval(drawChart, 1000);

    return () => {
      clearInterval(intervalId);
    };
  });

  useUpdateEffect(() => {
    satellitesRef.current = satellites;
  }, [satellites]);

  const drawChart = () => {
    // 차트 존재 시
    if (chart.current) {
      updateChart();
    }
    // 차트 미존재 시
    else {
      const initialData = Array(TICK_COUNT).fill(null);

      const context = chartRef.current.getContext('2d');
      const options = {
        type: 'line',
        data: {
          labels: Array(TICK_COUNT).fill(''),
          datasets: [
            {
              label: 'Satellites',
              data: initialData.slice(),
              borderColor: getCSSVariable('--tint-color'),
              borderWidth: 1,
              pointRadius: 1,
              pointBackgroundColor: getCSSVariable('--tint-color'),
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          scales: {
            xAxis: { display: false },
            yAxis: {
              min: 0,
              grid: {
                color: getCSSVariable('--line-color'),
                drawTicks: false,
                lineWidth: 0.5,
              },
              ticks: {
                color: 'white',
                font: { size: 8 },
                stepSize: 1,
              },
            },
          },
          plugins: { legend: false },
          animation: { easing: 'linear' },
        },
      };

      chart.current = new ChartJs(context, options);
    }
  };

  const updateChart = () => {
    const { labels, datasets } = chart.current.data;

    // X축 구간 수 동일한 경우, 첫 번째 데이터 제거
    if (TICK_COUNT === labels.length) {
      labels.shift();
      datasets.forEach((dataset) => dataset.data.shift());
    }

    labels.push('');
    datasets.forEach((dataset) => dataset.data.push(satellitesRef.current));

    // 차트 반영
    chart.current.update();
  };

  return (
    <div className={cx('container')}>
      <div className={cx('columns')}>
        <MiniTable
          title="Position"
          data={[
            { label: 'Latitude', value: latitude },
            { label: 'Longitude', value: longitude },
            { label: 'Altitude', value: altitude.toFixed(2) },
          ]}
        />
        {showData && (
          <MiniTable
            title="Data"
            data={[
              { label: 'Eph', value: eph },
              { label: 'Epv', value: epv },
              { label: 'Timestamp', value: time },
            ]}
          />
        )}
      </div>
      <div className={cx('chartWrapper')}>
        <div className={cx('title')}>Satellites</div>
        <div className={cx('chart')}>
          <canvas ref={chartRef} />
        </div>
      </div>
    </div>
  );
};

export default GPSRaw;
